// @flow

import type {
  FilterBlockType,
  FilterDataType,
  FilterFilteredDataType,
  FilterFunctionalData
} from './Redux/Types/FilterStateType'
import type { ImmutableType } from './Types/ImmutableTypes'
import type { InsuranceCompaniesType } from './Types/InsuranceCompanyTypes'
import type { QuoteFilterBlockType } from './Types/FilterTypes'
import type { QuoteRestrictionsType } from './Types/QuoteType'

export const BLOCK_TYPES = {
  VALUE: 0,
  RANGE: 1,
  HAS_VALUE: 2,
  KEY_VALUE: 3,
  KEY_VALUE_IS: 4,
  DO_NOTHING: 5,
  IN_ARRAY: 6,
  ONE_OF_PATHS: 7,
}

const getActiveFilters = (filterBlocks: Array<FilterBlockType>, filterData: FilterDataType): Array<FilterBlockType> =>
  filterBlocks ? filterBlocks.reduce((acc, filterBlock) => {
    if (filterData[filterBlock.name]) {
      const newFilterBlock = { ...filterBlock, options: [] }
      filterBlock.options.forEach((option) => {
        if (filterData[filterBlock.name][option.key]) {
          newFilterBlock.options.push(option)
        }
      })
      if (newFilterBlock.options.length > 0) {
        acc.push(newFilterBlock)
      }
    }
    return acc
  }, []) : []

export const getValueForPath = (pathArray: string | Array<string | number>, index: number, item: Object): any => {
  if (typeof pathArray === 'string') {
    return getValueForPath(pathArray.split('.'), index, item)
  }

  if (typeof item === 'undefined' || !pathArray || typeof item[pathArray[index]] === 'undefined') {
    return null
  }

  if (pathArray.length - 1 === index) {
    return item[pathArray[index]]
  }

  return getValueForPath(pathArray, index + 1, item[pathArray[index]])
}

const processOneBlock = (filterKeyPath: string | Array<string>, filterBlock: FilterBlockType,
                         dataToFilter: Array<any>): FilterFilteredDataType =>
  Object.keys(dataToFilter.reduce((acc, filteredItem) => {
    const key = getValueForPath(filterKeyPath, 0, filteredItem)
    const value = filterBlock.path !== ''
      ? getValueForPath(filterBlock.path, 0, filteredItem)
      : filteredItem

    let hasValueFilter = {}
    let pushEmpty = false
    let operator = ['OR', 'AND'].includes(filterBlock.operator) ? filterBlock.operator : 'AND'
    filterBlock.options.forEach((option) => {
      if (filterBlock.type === BLOCK_TYPES.VALUE && option.value === value) {
        acc[key] = true
      } else if (filterBlock.type === BLOCK_TYPES.RANGE && (!option.start || parseInt(value, 10) >= option.start) &&
        (!option.end || parseInt(value, 10) <= option.end)
      ) {
        acc[key] = true
      } else if (filterBlock.type === BLOCK_TYPES.HAS_VALUE) {
        if (!!value[option.value] && (operator === 'OR' || !pushEmpty)) {
          hasValueFilter[key] = true
        } else if (operator === 'AND' && !value[option.value]) {
          hasValueFilter = {}
          pushEmpty = true
        }
      } else if (filterBlock.type === BLOCK_TYPES.KEY_VALUE) {
        const subValue = getValueForPath(option.key, 0, value)
        if (subValue && !!subValue[option.valueKey || option.value] && (operator === 'OR' || !pushEmpty)) {
          hasValueFilter[key] = true
        } else if (operator === 'AND' && (!subValue || !subValue[option.valueKey || option.value])) {
          hasValueFilter = {}
          pushEmpty = true
        }
      } else if (filterBlock.type === BLOCK_TYPES.KEY_VALUE_IS) {
        const subValue = getValueForPath(option.valueKey, 0, value)
        if (subValue && subValue === option.value && (operator === 'OR' || !pushEmpty)) {
          hasValueFilter[key] = true
        } else if (operator === 'AND' && (!subValue || subValue !== option.value)) {
          hasValueFilter = {}
          pushEmpty = true
        }
      } else if (filterBlock.type === BLOCK_TYPES.DO_NOTHING) {
        hasValueFilter[key] = true
      } else if (filterBlock.type === BLOCK_TYPES.IN_ARRAY) {
        if (Array.isArray(value) && value.includes(option.key) && (operator === 'OR' || !pushEmpty)) {
          hasValueFilter[key] = true
        } else if (operator === 'AND' && (!value[option.value])) {
          hasValueFilter = {}
          pushEmpty = true
        }
      } else if (filterBlock.type === BLOCK_TYPES.ONE_OF_PATHS) {
        if (!pushEmpty && option.paths.filter(path => getValueForPath(path, 0, value)).length > 0) {
          hasValueFilter[key] = true
        } else {
          hasValueFilter = {}
          pushEmpty = true
        }
      }
    })
    acc = { ...acc, ...hasValueFilter }
    return acc
  }, {}))

export const actionFilterData = (filterKeyPath: string | Array<string>, filterBlocks: Array<FilterBlockType>,
                                 filterData: FilterDataType, dataToFilter: Array<any>): FilterFilteredDataType => {
  const activeFilters = getActiveFilters(filterBlocks, filterData)
  let filteredData = []
  activeFilters.forEach((filterBlock, index) => {
    if (index === 0) {
      filteredData = processOneBlock(filterKeyPath, filterBlock, dataToFilter)
    } else {
      const fData = processOneBlock(filterKeyPath, filterBlock, dataToFilter)
      filteredData = filteredData.filter((item) => fData.includes(item))
    }
  })
  // when filters are not active, show all
  if (dataToFilter && activeFilters.length === 0) {
    dataToFilter.forEach((filteredItem) => {
      const key = getValueForPath(filterKeyPath, 0, filteredItem)
      filteredData.push(key)
    })
  }
  return filteredData
}

export const functionalFilterDataCheck = (filterBlocks: Array<FilterBlockType>, functionalData: FilterFunctionalData & ImmutableType,
                                          dataToFilter: Array<any>, turnOffFetchingForKeys?: Array<string>,
                                          onlyTurOffFetching?: Array<string>) => {
  if (!filterBlocks) {
    return functionalData
  }

  filterBlocks.forEach((block) => {
    if (block.fetching) {
      if (onlyTurOffFetching) {
        onlyTurOffFetching.forEach((key) => {
          functionalData = functionalData.setIn([block.name, key, 'fetching'], false).setIn([block.name, key, 'enabled'], false)
        })
      }
      dataToFilter.forEach((dataItem) => {
        const key = getValueForPath(block.path, 0, dataItem)
        functionalData = functionalData.setIn([block.name, key, 'fetching'], false).setIn([block.name, key, 'enabled'], true)
      })
      if (turnOffFetchingForKeys) {
        turnOffFetchingForKeys.forEach((key) => {
          functionalData = functionalData.setIn([block.name, key, 'fetching'], false).setIn([block.name, key, 'enabled'], true)
        })
      }
    }
  })
  return functionalData
}

export const getValidCalculatorsForProductName = (insuranceCompanies: InsuranceCompaniesType, productName: string,
                                                  quoteRestrictions: QuoteRestrictionsType, isBackOffice: boolean) =>
  Object.keys(insuranceCompanies).reduce((calculatorsAcc, insuranceCompanyKey) => {
    if (!insuranceCompanies[insuranceCompanyKey].calculators || insuranceCompanies[insuranceCompanyKey].calculators.length === 0) {
      return calculatorsAcc
    }
    const regExp = new RegExp(`^${productName}`, 'i')
    return [
      ...calculatorsAcc,
      ...insuranceCompanies[insuranceCompanyKey].calculators.filter(calculator => regExp.test(calculator) && (isBackOffice ||
        !quoteRestrictions || !quoteRestrictions.isCallback.includes(insuranceCompanyKey))
      )
    ]
  }, [])

export const initQuoteFilters = (filterBlocks: QuoteFilterBlockType) =>
  Object.keys(filterBlocks).reduce((filterBlockAcc, tab) => {
    filterBlockAcc[tab] = Object.keys(filterBlocks[tab]).reduce((filterItemAcc, itemKey) => {
      filterItemAcc[itemKey] = filterBlocks[tab][itemKey].slice(0, -1)
      return filterItemAcc
    }, {})
    return filterBlockAcc
  }, {})

const LIMITS = {
  low: 0,
  normal: 1,
  high: 2,
  theft: 10,
  accident: 11,
  allRisk: 12,
  allRiskGap: 13
}

export const sortLimits = (limitsArray: Array<string>) => {
  limitsArray.sort((a: string, b: string) => LIMITS[a] - LIMITS[b])
}

export const getYearPrice = (paymentPeriod: string, pricePerYear: number): number => {
  switch (paymentPeriod) {
    case 'semi-annually':
      return pricePerYear *= 2
    case 'quarterly':
      return pricePerYear *= 4
    case 'monthly':
      return pricePerYear *= 12
    default:
      return pricePerYear
  }
}
